import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { useCustomerAuth } from '../hooks/useCustomerAuth';
import './PublicHeader.css';

const WHATSAPP_PHONE = '972585576176';
const WHATSAPP_BOT_URL = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent('hello')}`;
const WHATSAPP_CONTACT_URL = `https://wa.me/${WHATSAPP_PHONE}`;
const LIGHT_LOGO_SRC = '/updated%20dark%20mode%20logo.png';
const DARK_LOGO_SRC = '/updated%20dark%20mode%20logo.png';

export default function PublicHeader({ theme, onToggleTheme }) {
  const location = useLocation();
  const path = location?.pathname || '/';
  const hideOnPages = path.startsWith('/register');
  const isAuthPage = path.startsWith('/login') || path.startsWith('/register');
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);
  const isDark = theme === 'dark';
  const headerLogoSrc = isDark ? DARK_LOGO_SRC : LIGHT_LOGO_SRC;
  const { isLoggedIn, undismiss } = useCustomerAuth();
  const navigate = useNavigate();

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [path]);

  // Lock body scroll, close on outside scroll / touch
  useEffect(() => {
    if (!mobileOpen) return;

    const scrollY = window.scrollY;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    const close = () => setMobileOpen(false);
    const handlePointerDown = (event) => {
      const drawer = drawerRef.current;
      const hamburger = hamburgerRef.current;
      const target = event.target;

      if (drawer && drawer.contains(target)) return;
      if (hamburger && hamburger.contains(target)) return;

      close();
    };

    // Close on any scroll attempt (e.g. programmatic or bubbled)
    window.addEventListener('scroll', close, { passive: true });
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', close);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  if (hideOnPages) return null;

  const isHome = path === '/';
  const showGuestLoginLink = !isLoggedIn && !isAuthPage;

  return (
    <header className="ph">
      <div className={isHome ? 'ph-inner ph-inner--home' : 'ph-inner'}>
        <Link to="/" className="ph-brand" aria-label="תן לי תור">
          <img
            src={headerLogoSrc}
            alt=""
            className="ph-brand-banner"
            width="2402"
            height="1760"
            decoding="async"
            fetchPriority="high"
            aria-hidden
          />
        </Link>

        {/* Right-side group: login + hamburger */}
        <div className="ph-right-group">
          {isHome && (
            isLoggedIn ? (
              <button
                type="button"
                className="ph-login-btn ph-login-btn--home"
                onClick={() => navigate('/my-businesses')}
                  aria-label="עסקים שנרשמתי אצלם"
              >
                👤
              </button>
            ) : (
              <button
                type="button"
                className="ph-login-btn ph-login-btn--home"
                onClick={() => undismiss()}
                aria-label="התחברות לקוחות"
              >
                התחברות לקוחות
              </button>
            )
          )}
          {/* Hamburger – always visible (home = top-right, other pages = in nav row) */}
          <button
            ref={hamburgerRef}
            className={isHome ? 'ph-hamburger ph-hamburger--home' : 'ph-hamburger'}
            onClick={() => setMobileOpen((v) => !v)}
            type="button"
            aria-label="תפריט"
            aria-expanded={mobileOpen}
          >
            ☰
          </button>
        </div>

        {!isHome && (
          <>
            {/* Desktop nav */}
            <nav className="ph-nav" aria-label="קישורים">
              <Link to="/catalog" className="ph-link">קטלוג</Link>
              <Link to="/articles" className="ph-link">מאמרים</Link>
              <Link to="/find2" className="ph-link">מפה</Link>
              {isLoggedIn && (
                <Link to="/my-businesses" className="ph-link ph-link--customer">עסקים שנרשמתי אצלם</Link>
              )}
              <a
                href={WHATSAPP_CONTACT_URL}
                className="ph-link ph-link--whatsapp"
                target="_blank"
                rel="noreferrer"
              >
                <FaWhatsapp aria-hidden /> צור קשר
              </a>
            </nav>
          </>
        )}
      </div>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div className="ph-mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}
      <div ref={drawerRef} className={mobileOpen ? 'ph-mobile-drawer open' : 'ph-mobile-drawer'}>
        <div className="ph-mobile-header">
          <span className="ph-mobile-title">תפריט</span>
          <button className="ph-mobile-close" type="button" onClick={() => setMobileOpen(false)} aria-label="סגור">✕</button>
        </div>
        <nav className="ph-mobile-links">
          <Link to="/" className="ph-mobile-link" onClick={() => setMobileOpen(false)}>🏠 דף הבית</Link>
          <Link to="/catalog" className="ph-mobile-link" onClick={() => setMobileOpen(false)}>📋 קטלוג</Link>
          <Link to="/find2" className="ph-mobile-link" onClick={() => setMobileOpen(false)}>🗺️ מפה</Link>
          <Link to="/open-business" className="ph-mobile-link" onClick={() => setMobileOpen(false)}>🏪 פתיחת עסק</Link>
          {isLoggedIn && (
            <Link to="/my-businesses" className="ph-mobile-link ph-mobile-link--customer" onClick={() => setMobileOpen(false)}>💼 עסקים שנרשמתי אצלם</Link>
          )}
          {showGuestLoginLink && (
            <Link to="/login" className="ph-mobile-link ph-mobile-link--primary" onClick={() => setMobileOpen(false)}>🔑 כניסה</Link>
          )}
          <Link to="/articles" className="ph-mobile-link" onClick={() => setMobileOpen(false)}>📰 מאמרים</Link>
        </nav>
        <div className="ph-mobile-preferences">
          <div className="ph-mobile-preferences__label-group">
            <span className="ph-mobile-preferences__title">ערכת תצוגה</span>
            <span className="ph-mobile-preferences__value">{isDark ? 'כהה' : 'בהירה'}</span>
          </div>
          <button
            type="button"
            className="ph-theme-toggle ph-theme-toggle--drawer"
            onClick={onToggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'מצב בהיר' : 'מצב כהה'}
          >
            <span className="ph-theme-toggle__track">
              <span className={`ph-theme-toggle__thumb ${isDark ? 'ph-theme-toggle__thumb--dark' : ''}`} />
              <span className="ph-theme-toggle__icon ph-theme-toggle__icon--sun" aria-hidden>☀️</span>
              <span className="ph-theme-toggle__icon ph-theme-toggle__icon--moon" aria-hidden>🌙</span>
            </span>
          </button>
        </div>
        <div className="ph-mobile-actions">
          <a
            href={WHATSAPP_BOT_URL}
            className="ph-mobile-action ph-mobile-action--bot"
            target="_blank"
            rel="noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            <FaWhatsapp aria-hidden />
            <span>בוט וואטסאפ</span>
          </a>
          <a
            href={WHATSAPP_CONTACT_URL}
            className="ph-mobile-action ph-mobile-action--contact"
            target="_blank"
            rel="noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            <FaWhatsapp aria-hidden />
            <span>צור קשר</span>
          </a>
        </div>
      </div>
    </header>
  );
}
